import { clamp } from '../util/units.js'

/**
 * Everything the pilot can actually change, and how long it takes to happen.
 *
 * On rails the aeroplane's *path* is not yours, but its *configuration* is
 * entirely yours — and configuration is the whole subject of a procedures
 * trainer. So this is deliberately not a set of booleans. Gear takes six
 * seconds to come down, flaps four, the hook two, and the throttle is a lever
 * you move rather than a switch you flip.
 *
 * That matters for teaching. If the gear snapped down the instant you pressed
 * G, "gear down at the abeam" would feel like a formality. Six seconds of
 * travel is why you do it early, and why the checklist is ordered the way it
 * is.
 */

/** Seconds from commanded to fully travelled. */
export const TRAVEL_S = {
  gear: 6.0,
  flaps: 4.0,
  hook: 2.0,
  speedbrake: 1.6,
}

/** Throttle lever travel, idle to military, in seconds. */
export const THROTTLE_TRAVEL_S = 2.4

export class PilotControls {
  constructor() {
    this.reset()
  }

  reset({ dirty = false } = {}) {
    this.gearCmd = dirty ? 1 : 0
    this.flapCmd = dirty ? 1 : 0
    this.hookCmd = dirty ? 1 : 0
    this.speedbrakeCmd = 0

    this.gearNorm = this.gearCmd
    this.flapNorm = this.flapCmd
    this.hookNorm = this.hookCmd
    this.speedbrakeNorm = 0

    /** 0 is idle, 1 is military. The AB detent is above 0.98. */
    this.throttle = dirty ? 0.68 : 0.62
    this.wheelBrakes = false

    /**
     * Direct lift control. The F-14's spoilers, used as a fine vertical trim
     * on approach: the thumbwheel moves the aeroplane up and down without
     * changing attitude or power, which is why it is armed on the downwind and
     * why an F-14 flies the ball differently to everything else on the boat.
     */
    this.dlcArmed = dirty
    this.dlcNorm = 0

    /** Approach power compensator — the autothrottle. "If desired". */
    this.apcEngaged = false

    /** Stability augmentation. On before the break, per the chart. */
    this.sasOn = dirty

    /** Set the moment the ball is called. There is only one call in a pass. */
    this.ballCalled = false
    this.ballCallAt = null

    this._held = new Set()
  }

  /* =================================================================== *
   *  BIND HANDLING                                                      *
   * =================================================================== */

  /**
   * @param {string} bindId a key from src/input/DcsKeymap.js
   * @param {object} ctx    { simTime, inGroove }
   * @returns {string|null} a short message worth flashing, or null
   */
  press(bindId, ctx = {}) {
    switch (bindId) {
      case 'gearToggle':
        this.gearCmd = this.gearCmd > 0.5 ? 0 : 1
        return this.gearCmd ? 'GEAR DOWN' : 'GEAR UP'
      case 'gearDown':
        this.gearCmd = 1
        return 'GEAR DOWN'
      case 'gearUp':
        this.gearCmd = 0
        return 'GEAR UP'

      case 'flapsToggle':
        this.flapCmd = this.flapCmd > 0.5 ? 0 : 1
        return this.flapCmd ? 'FLAPS DOWN' : 'FLAPS UP'
      case 'flapsDown':
        this.flapCmd = 1
        return 'FLAPS DOWN'
      case 'flapsUp':
        this.flapCmd = 0
        return 'FLAPS UP'

      case 'brakeToggle':
        this.speedbrakeCmd = this.speedbrakeCmd > 0.5 ? 0 : 1
        return this.speedbrakeCmd ? 'SPEEDBRAKE OUT' : 'SPEEDBRAKE IN'
      case 'brakeOut':
        this.speedbrakeCmd = 1
        return 'SPEEDBRAKE OUT'
      case 'brakeIn':
        this.speedbrakeCmd = 0
        return 'SPEEDBRAKE IN'

      case 'hook':
        this.hookCmd = this.hookCmd > 0.5 ? 0 : 1
        return this.hookCmd ? 'HOOK DOWN' : 'HOOK UP'

      case 'dlc':
        // Arming needs the flaps out — the spoilers are the DLC surfaces and
        // they are not available clean.
        if (this.flapCmd < 0.5) return 'FLAPS FIRST'
        this.dlcArmed = !this.dlcArmed
        if (!this.dlcArmed) this.dlcNorm = 0
        return this.dlcArmed ? 'DLC ARMED' : 'DLC OFF'

      case 'autoThrottle':
        if (this.gearCmd < 0.5) return 'GEAR FIRST'
        this.apcEngaged = !this.apcEngaged
        return this.apcEngaged ? 'AUTO THROTTLE ENGAGED' : 'AUTO THROTTLE OFF'

      case 'sas':
        this.sasOn = !this.sasOn
        return this.sasOn ? 'SAS ON' : 'SAS OFF'

      case 'comms':
        if (this.ballCalled) return null
        this.ballCalled = true
        this.ballCallAt = ctx.simTime ?? 0
        return 'BALL CALL'

      default:
        return null
    }
  }

  hold(bindId, down) {
    if (down) this._held.add(bindId)
    else this._held.delete(bindId)
    if (bindId === 'wheelBrakes') this.wheelBrakes = down
  }

  isHeld(id) {
    return this._held.has(id)
  }

  /* =================================================================== *
   *  TRAVEL                                                             *
   * =================================================================== */

  update(dt) {
    const move = (now, cmd, seconds) => {
      const rate = dt / Math.max(seconds, 0.01)
      return clamp(now + clamp(cmd - now, -rate, rate), 0, 1)
    }

    this.gearNorm = move(this.gearNorm, this.gearCmd, TRAVEL_S.gear)
    this.flapNorm = move(this.flapNorm, this.flapCmd, TRAVEL_S.flaps)
    this.hookNorm = move(this.hookNorm, this.hookCmd, TRAVEL_S.hook)
    this.speedbrakeNorm = move(this.speedbrakeNorm, this.speedbrakeCmd, TRAVEL_S.speedbrake)

    const throttleRate = dt / THROTTLE_TRAVEL_S
    if (this._held.has('throttleUp')) this.throttle = clamp(this.throttle + throttleRate, 0, 1)
    if (this._held.has('throttleDown')) this.throttle = clamp(this.throttle - throttleRate, 0, 1)
  }

  /** Copy the configuration into the state object the scene reads. */
  writeInto(state) {
    state.gearNorm = this.gearNorm
    state.flapNorm = this.flapNorm
    state.flapDeg = this.flapNorm * 35
    state.hookNorm = this.hookNorm
    state.speedbrakeNorm = this.speedbrakeNorm
    state.dlcNorm = this.dlcNorm
    state.throttleNorm = this.throttle
    state.augmenting = this.throttle > 0.985
    state.n2 = 62 + this.throttle * 38
    state.thrustLbs = this.throttle * 27000
    // W is advertised as the wheel brakes because that is what DCS binds it
    // to. It has nothing to slow down here — the wire does that — but it must
    // at least be visible somewhere, or the key list is promising something
    // that does not exist.
    state.wheelBrakes = this.wheelBrakes
    return state
  }

  get snapshot() {
    return {
      throttleLever: this.throttle,
      afterburner: this.throttle > 0.985,
      gearDown: this.gearCmd > 0.5,
      gearNorm: this.gearNorm,
      flapCmd: this.flapCmd,
      flapNorm: this.flapNorm,
      hookDown: this.hookCmd > 0.5,
      hookNorm: this.hookNorm,
      speedbrake: this.speedbrakeCmd > 0.5,
      speedbrakeNorm: this.speedbrakeNorm,
      wheelBrakes: this.wheelBrakes,
      ballCalled: this.ballCalled,
      dlc: this.dlcNorm,
      dlcArmed: this.dlcArmed,
      sasOn: this.sasOn,
      sweepManual: false,
      apcEngaged: this.apcEngaged,
      pitchTrim: 0,
    }
  }
}
